import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Settings, Edit, Calculator, History, ShieldAlert, WalletCards, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHelpers';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPricingConfig = () => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [newCredits, setNewCredits] = useState(0);
  const [reason, setReason] = useState('');
  const [walletSettings, setWalletSettings] = useState(null);
  const [calculatorCredits, setCalculatorCredits] = useState(200);
  const [classificationReport, setClassificationReport] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');
  const [brdPricing, setBrdPricing] = useState(null);
  const [brdEditing, setBrdEditing] = useState(null);
  const [brdBasePrice, setBrdBasePrice] = useState('');
  const [brdReason, setBrdReason] = useState('');

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/wallet/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPricing(response.data);
      setWalletSettings(response.data.wallet_module_settings?.legal_opinions || null);

      const classificationResponse = await axios.get(`${API}/api/wallet/balance-classification-report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setClassificationReport(classificationResponse.data);
      const brdResponse = await axios.get(`${API}/api/admin/brd-service-pricing`, { headers: { 'Authorization': `Bearer ${token}` } });
      setBrdPricing(brdResponse.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast.error(getErrorMessage(error, 'Failed to load pricing configuration'));
    } finally {
      setLoading(false);
    }
  };

  const openBrdEditor = (category) => { setBrdEditing(category); setBrdBasePrice(category.configuration?.base_price_inr || ''); setBrdReason(''); };
  const saveBrdPricing = async () => {
    if (!brdEditing) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/brd-service-pricing/${brdEditing.category}`, { base_price_inr: Number(brdBasePrice), reason: brdReason, is_active: true }, { headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('BRD category pricing saved'); setBrdEditing(null); fetchPricing();
    } catch (error) { toast.error(getErrorMessage(error, 'Unable to save BRD category pricing')); }
  };

  const handleEditPricing = (service) => {
    setEditingService(service);
    setNewCredits(service.credits_per_operation);
    setReason('');
    setShowEditModal(true);
  };

  const handleUpdatePricing = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/admin/wallet/pricing/service/${editingService.service_type}`,
        {
          credits_per_operation: parseFloat(newCredits),
          reason: reason
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(`Pricing updated for ${editingService.service_type}`);
      setShowEditModal(false);
      fetchPricing();
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error(getErrorMessage(error, 'Failed to update pricing'));
    }
  };

  const handleWalletSettingsSave = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/admin/wallet/pricing`,
        {
          module: 'legal_opinions',
          price_per_credit: parseFloat(walletSettings.price_per_credit),
          minimum_credits: parseInt(walletSettings.minimum_credits, 10),
          gst_enabled: Boolean(walletSettings.gst_enabled),
          gst_rate: parseFloat(walletSettings.gst_rate),
          reason: 'Admin wallet settings update'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Wallet settings updated');
      fetchPricing();
    } catch (error) {
      console.error('Error updating wallet settings:', error);
      toast.error(getErrorMessage(error, 'Failed to update wallet settings'));
    }
  };

  const calculatorPreview = useMemo(() => {
    if (!walletSettings) return null;
    const credits = Math.max(0, Number(calculatorCredits) || 0);
    const pricePerCredit = Number(walletSettings.price_per_credit || 0);
    const gstRate = walletSettings.gst_enabled ? Number(walletSettings.gst_rate || 0) : 0;
    const baseAmount = credits * pricePerCredit;
    const gstAmount = (baseAmount * gstRate) / 100;
    return {
      baseAmount,
      gstAmount,
      totalAmount: baseAmount + gstAmount,
    };
  }, [walletSettings, calculatorCredits]);

  const riskSummary = classificationReport?.migration_status;

  const renderRiskBadge = (riskLevel) => {
    const styles = {
      HIGH: 'bg-red-100 text-red-700 border-red-200',
      MEDIUM: 'bg-amber-100 text-amber-700 border-amber-200',
      LOW: 'bg-green-100 text-green-700 border-green-200'
    };
    return (
      <Badge className={styles[riskLevel] || 'bg-slate-100 text-slate-700 border-slate-200'}>
        {riskLevel}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const { price_per_credit_inr, service_pricing } = pricing || {};

  const serviceLabels = {
    retail: 'Retail Legal Opinion',
    apf: 'APF Legal Opinion',
    ec_extractor: 'EC Extractor',
    brd_missing_documents_report: 'Missing Documents — AI-generated report (BRD)',
    expert_review_service_a: 'Missing Docs Expert Review — Service A (BRD)'
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Settings className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Wallet Administration</h1>
              <p className="text-sm text-gray-500">Settings, audit history, and migration risk review in one place.</p>
            </div>
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" data-testid="wallet-audit-history-open-button">
                <History className="mr-2 h-4 w-4" />
                Open Audit Drawer
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="wallet-audit-history-drawer">
              <SheetHeader>
                <SheetTitle>Wallet Settings Change History</SheetTitle>
                <SheetDescription>
                  Review who changed wallet purchase settings, when they changed, and the before/after values.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                {(pricing?.wallet_settings_audit_history || []).length === 0 ? (
                  <div className="rounded-2xl border border-dashed p-6 text-sm text-gray-500">
                    No wallet settings history yet.
                  </div>
                ) : (
                  pricing.wallet_settings_audit_history.slice().reverse().map((entry, index) => (
                    <div key={`${entry.updated_at}-${index}`} className="rounded-2xl border bg-white p-4 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">Changed by: {entry.updated_by || 'Unknown'}</div>
                          <div className="text-xs text-slate-500">{new Date(entry.updated_at).toLocaleString('en-IN')}</div>
                        </div>
                        <Badge className="bg-slate-100 text-slate-700 border-slate-200">{entry.reason || 'No reason recorded'}</Badge>
                      </div>
                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <div className="rounded-xl bg-slate-50 p-3 text-sm">
                          <div className="text-slate-500">Price Per Credit</div>
                          <div className="font-semibold text-slate-900">₹{entry.previous?.price_per_credit} → ₹{entry.next?.price_per_credit}</div>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3 text-sm">
                          <div className="text-slate-500">Minimum Credits</div>
                          <div className="font-semibold text-slate-900">{entry.previous?.minimum_credits} → {entry.next?.minimum_credits}</div>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3 text-sm">
                          <div className="text-slate-500">GST Enabled</div>
                          <div className="font-semibold text-slate-900">{String(entry.previous?.gst_enabled)} → {String(entry.next?.gst_enabled)}</div>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3 text-sm">
                          <div className="text-slate-500">GST Rate</div>
                          <div className="font-semibold text-slate-900">{entry.previous?.gst_rate}% → {entry.next?.gst_rate}%</div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 bg-white" data-testid="wallet-admin-tabs">
            <TabsTrigger value="settings" data-testid="wallet-admin-tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="audit" data-testid="wallet-admin-tab-audit">Audit History</TabsTrigger>
            <TabsTrigger value="risk" data-testid="wallet-admin-tab-risk-review">Risk Review</TabsTrigger>
            <TabsTrigger value="brd" data-testid="wallet-admin-tab-brd-service-pricing">BRD Service Pricing</TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="space-y-6">

        {/* Wallet Purchase Settings */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
            <CardTitle>💎 Global Wallet Purchase Settings</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="wallet-price-per-credit">Price Per Credit (₹)</Label>
                <Input
                  id="wallet-price-per-credit"
                  data-testid="wallet-price-per-credit-input"
                  type="number"
                  value={walletSettings?.price_per_credit || ''}
                  onChange={(e) => setWalletSettings((prev) => ({ ...prev, price_per_credit: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="wallet-minimum-credits">Minimum Wallet Purchase Credits</Label>
                <Input
                  id="wallet-minimum-credits"
                  data-testid="wallet-minimum-credits-input"
                  type="number"
                  value={walletSettings?.minimum_credits || ''}
                  onChange={(e) => setWalletSettings((prev) => ({ ...prev, minimum_credits: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="wallet-gst-rate">GST Rate (%)</Label>
                <Input
                  id="wallet-gst-rate"
                  data-testid="wallet-gst-rate-input"
                  type="number"
                  value={walletSettings?.gst_rate || ''}
                  onChange={(e) => setWalletSettings((prev) => ({ ...prev, gst_rate: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-blue-100 bg-blue-50 p-4">
              <div>
                <div className="text-sm text-blue-700">GST Enabled</div>
                <div className="text-xs text-blue-600">Recharge applies GST, wallet balance stays credits-only.</div>
              </div>
              <Button
                type="button"
                variant={walletSettings?.gst_enabled ? 'default' : 'outline'}
                data-testid="wallet-gst-toggle-button"
                className={walletSettings?.gst_enabled ? 'bg-slate-900 text-white hover:bg-slate-800' : 'border-slate-300 text-slate-700'}
                onClick={() => setWalletSettings((prev) => ({ ...prev, gst_enabled: !prev.gst_enabled }))}
              >
                {walletSettings?.gst_enabled ? 'Enabled' : 'Disabled'}
              </Button>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleWalletSettingsSave} data-testid="save-wallet-settings-button" className="bg-slate-900 text-white hover:bg-slate-800">
                Save Wallet Settings
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              Live Recharge Calculator
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="calculator-credits">Credits</Label>
                <Input
                  id="calculator-credits"
                  data-testid="wallet-live-calculator-credits-input"
                  type="number"
                  value={calculatorCredits}
                  onChange={(e) => setCalculatorCredits(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-3 gap-3 rounded-2xl border bg-slate-50 p-4 text-sm">
                <div>
                  <div className="text-slate-500">Base</div>
                  <div className="font-semibold text-slate-900">₹{calculatorPreview?.baseAmount?.toLocaleString('en-IN') || 0}</div>
                </div>
                <div>
                  <div className="text-slate-500">GST</div>
                  <div className="font-semibold text-slate-900">₹{calculatorPreview?.gstAmount?.toLocaleString('en-IN') || 0}</div>
                </div>
                <div>
                  <div className="text-slate-500">Total</div>
                  <div className="font-semibold text-blue-700" data-testid="wallet-live-calculator-total">₹{calculatorPreview?.totalAmount?.toLocaleString('en-IN') || 0}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Pricing */}
        <Card>
          <CardHeader>
            <CardTitle>📊 Service Pricing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {service_pricing?.map((service) => (
              <div
                key={service.service_type}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex-1">
                  <div className="font-semibold text-gray-800">
                    {serviceLabels[service.service_type] || service.service_type}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {service.description}
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-800">
                      {service.credits_per_operation} {service.credits_per_operation === 1 ? 'credit' : 'credits'}
                    </div>
                    <div className="text-sm text-gray-500">
                      ₹{service.cost_inr.toLocaleString('en-IN')} per operation
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditPricing(service)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="text-sm text-yellow-800">
              <strong>⚠️ Important:</strong> Changes to pricing will take effect immediately for all new operations.
              Existing operations will not be affected.
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="audit" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5 text-slate-700" />
                  Wallet Settings Audit History
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(pricing?.wallet_settings_audit_history || []).length === 0 ? (
                  <div className="rounded-2xl border border-dashed p-6 text-sm text-gray-500">
                    No audit history is available yet.
                  </div>
                ) : (
                  pricing.wallet_settings_audit_history.slice().reverse().map((entry, index) => (
                    <div key={`${entry.updated_at}-${index}`} className="rounded-2xl border bg-white p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-slate-900">{entry.updated_by || 'Unknown user'}</div>
                          <div className="text-xs text-slate-500">{new Date(entry.updated_at).toLocaleString('en-IN')}</div>
                        </div>
                        <div className="text-xs text-slate-600">{entry.reason || 'No reason recorded'}</div>
                      </div>
                      <div className="mt-4 grid gap-3 md:grid-cols-4">
                        <div className="rounded-xl bg-slate-50 p-3 text-sm"><div className="text-slate-500">Price Per Credit</div><div className="font-semibold">₹{entry.previous?.price_per_credit} → ₹{entry.next?.price_per_credit}</div></div>
                        <div className="rounded-xl bg-slate-50 p-3 text-sm"><div className="text-slate-500">Minimum Credits</div><div className="font-semibold">{entry.previous?.minimum_credits} → {entry.next?.minimum_credits}</div></div>
                        <div className="rounded-xl bg-slate-50 p-3 text-sm"><div className="text-slate-500">GST Enabled</div><div className="font-semibold">{String(entry.previous?.gst_enabled)} → {String(entry.next?.gst_enabled)}</div></div>
                        <div className="rounded-xl bg-slate-50 p-3 text-sm"><div className="text-slate-500">GST Rate</div><div className="font-semibold">{entry.previous?.gst_rate}% → {entry.next?.gst_rate}%</div></div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="risk" className="space-y-6">
            <Card className="border-amber-200 bg-amber-50" data-testid="wallet-migration-status-banner">
              <CardContent className="p-5">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-700" />
                    <div>
                      <div className="text-sm font-semibold text-amber-900">Migration Status</div>
                      <div className="text-2xl font-bold text-amber-950">⚠ BLOCKED</div>
                      <p className="text-sm text-amber-800">{riskSummary?.message || 'Migration cannot proceed until manual approval.'}</p>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3 text-sm shadow-sm">
                    <div className="text-slate-500">Unknown Wallets</div>
                    <div className="text-2xl font-bold text-slate-900">{riskSummary?.blocked_unknown_wallets || 0}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5 text-red-600" />
                    Risk Review
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(classificationReport?.wallets || []).map((wallet) => (
                    <div key={wallet.wallet_id} className="rounded-2xl border bg-white p-4" data-testid={`wallet-risk-row-${wallet.tenant_id}`}>
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{wallet.tenant_id}</div>
                          <div className="text-xs text-slate-500">Wallet ID: {wallet.wallet_id}</div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className="bg-slate-100 text-slate-700 border-slate-200">{wallet.likely_unit}</Badge>
                          {renderRiskBadge(wallet.risk_level)}
                        </div>
                      </div>
                      <div className="mt-4 grid gap-3 md:grid-cols-3">
                        <div className="rounded-xl bg-slate-50 p-3 text-sm"><div className="text-slate-500">Current Balance</div><div className="font-semibold text-slate-900">{wallet.current_balance} Credits</div></div>
                        <div className="rounded-xl bg-slate-50 p-3 text-sm"><div className="text-slate-500">Classification</div><div className="font-semibold text-slate-900">{wallet.likely_unit}</div></div>
                        <div className="rounded-xl bg-slate-50 p-3 text-sm"><div className="text-slate-500">Confidence</div><div className="font-semibold text-slate-900">{Math.round((wallet.confidence || 0) * 100)}%</div></div>
                      </div>
                      <div className="mt-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Signals</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {(wallet.signals || []).length > 0 ? wallet.signals.map((signal) => (
                            <Badge key={signal} className="bg-red-50 text-red-700 border-red-200">✓ {signal}</Badge>
                          )) : <span className="text-sm text-slate-500">No signals found</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <WalletCards className="h-5 w-5 text-slate-700" />
                    Risk Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <div className="text-xs uppercase tracking-wide text-slate-500">Classification Summary</div>
                    <div className="mt-3 space-y-2 text-sm">
                      {Object.entries(classificationReport?.summary || {}).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-slate-600">{key}</span>
                          <span className="font-semibold text-slate-900">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-4 text-sm">
                    <div className="text-xs uppercase tracking-wide text-slate-500">EC Cost Audit</div>
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center justify-between"><span className="text-slate-600">Current EC Cost</span><span className="font-semibold text-slate-900">{classificationReport?.ec_cost_audit?.current_ec_cost} credits</span></div>
                      <div>
                        <div className="text-slate-600">Historical EC Debit Distribution</div>
                        <div className="mt-2 space-y-1">
                          {Object.entries(classificationReport?.ec_cost_audit?.historical_ec_debit_distribution || {}).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between rounded-xl bg-white px-3 py-2"><span>{key} credits</span><span className="font-semibold">{value}</span></div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="brd" className="space-y-6" data-testid="brd-service-pricing-tab"><Card><CardHeader><CardTitle>BRD Service Pricing</CardTitle></CardHeader><CardContent><p className="text-sm text-slate-600" data-testid="brd-service-pricing-wallet-source">Current generic credit value is read from the existing wallet configuration: ₹{brdPricing?.wallet_context?.credit_value_inr ?? '—'}. All six BRD services below deduct from the shared generic-credit wallet.</p><div className="mt-5 space-y-3" data-testid="brd-pricing-consumption-entries">{(service_pricing || []).filter((service) => ['brd_missing_documents_report', 'expert_review_service_a'].includes(service.service_type)).map((service) => <div key={service.service_type} className="flex flex-wrap items-center justify-between gap-4 border p-4" data-testid={`brd-pricing-service-${service.service_type}`}><div><p className="font-semibold">{serviceLabels[service.service_type] || service.service_type}</p><p className="mt-1 text-sm text-slate-600">{service.credits_per_operation} {service.credits_per_operation === 1 ? 'credit' : 'credits'} · ₹{service.cost_inr.toLocaleString('en-IN')} per operation</p><p className="mt-1 text-xs text-slate-500">{service.description}</p></div><Button variant="outline" onClick={() => handleEditPricing(service)} data-testid={`brd-pricing-service-edit-${service.service_type}`}><Edit className="mr-2 h-4 w-4" />Configure</Button></div>)}</div><div className="mt-5 space-y-3">{brdPricing?.categories?.map((category) => <div key={category.category} className="flex flex-wrap items-center justify-between gap-4 border p-4" data-testid={`brd-pricing-category-${category.category}`}><div><p className="font-semibold">Missing Docs + Legal Opinion (Service B) — {category.display_name}</p><p className="mt-1 text-sm text-slate-600">{category.configuration ? `₹${category.configuration.base_price_inr} · ${category.configuration.required_credits} generic credits · Version ${category.configuration.pricing_version}` : 'Not configured'}</p><p className="mt-1 text-xs text-slate-500">Wallet credit value snapshot: {category.configuration?.validated_credit_value_inr ?? 'Not yet captured'}</p></div><Button variant="outline" onClick={() => openBrdEditor(category)} data-testid={`brd-pricing-edit-${category.category}`}><Edit className="mr-2 h-4 w-4" />Configure</Button></div>)}</div></CardContent></Card></TabsContent>
        </Tabs>

        {/* Edit Pricing Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle>
                Update Pricing: {serviceLabels[editingService?.service_type]}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <div className="text-sm text-gray-600 mb-2">Current Pricing</div>
                <div className="text-2xl font-bold">
                  {editingService?.credits_per_operation} credits
                  <span className="text-lg text-gray-500 ml-2">
                    (₹{editingService?.cost_inr.toLocaleString('en-IN')})
                  </span>
                </div>
              </div>
              <div>
                <Label>New Credits Per Operation *</Label>
                <Input
                  type="number"
                  value={newCredits}
                  onChange={(e) => setNewCredits(e.target.value)}
                  min="0.1"
                  step="0.1"
                />
                <div className="text-sm text-gray-500 mt-1">
                  New cost: ₹{(newCredits * price_per_credit_inr).toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <Label>Reason for Change *</Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Market adjustment, Cost optimization"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdatePricing}
                disabled={!reason || reason.length < 3 || newCredits < 0.1}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Update Pricing
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={Boolean(brdEditing)} onOpenChange={(open) => !open && setBrdEditing(null)}><DialogContent className="bg-white max-w-md" data-testid="brd-pricing-edit-dialog"><DialogHeader><DialogTitle>Configure {brdEditing?.display_name}</DialogTitle></DialogHeader><div className="space-y-4 py-4"><div><Label htmlFor="brd-pricing-base-price">Base price (₹)</Label><Input id="brd-pricing-base-price" type="number" min="0" value={brdBasePrice} onChange={(event) => setBrdBasePrice(event.target.value)} data-testid="brd-pricing-base-price-input" /></div><div><Label htmlFor="brd-pricing-reason">Reason for change</Label><Input id="brd-pricing-reason" value={brdReason} onChange={(event) => setBrdReason(event.target.value)} data-testid="brd-pricing-reason-input" /></div><p className="text-sm text-slate-600" data-testid="brd-pricing-whole-credit-notice">The server validates this base price against the current wallet credit value and rejects fractional credit deductions.</p></div><DialogFooter><Button variant="outline" onClick={() => setBrdEditing(null)} data-testid="brd-pricing-cancel-button">Cancel</Button><Button onClick={saveBrdPricing} disabled={!brdBasePrice || brdReason.trim().length < 3} data-testid="brd-pricing-save-button">Save category pricing</Button></DialogFooter></DialogContent></Dialog>
      </div>
    </div>
  );
};

export default AdminPricingConfig;